/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";

import { useDispatch } from "react-redux";

import makeStyles from "@material-ui/styles/makeStyles";
import Typography from "@material-ui/core/Typography";

// jaza-soft
import { PageHeader, PageFooter, showNotification, SweetAlert, BackButton } from "jazasoft";

//helper
import { findDuplicates, isEmpty } from "../../utils/helpers";
import useQuery from "../../hooks/useQuery";
import useMutation from "../../hooks/useMutation";
// local
import MuiTable from "../../components/MuiTable";
import SettingKeys from "../../utils/SettingKeys";

const renderBooleanValue = value => {
  let str = "";
  if (value === "true" || value === true) {
    str = "Yes";
  } else if (value === "false" || value === false) {
    str = "No";
  } else {
    str = value;
  }
  return str;
};

const getValueList = value => {
  if (isEmpty(value)) return [];
  return Object.keys(value).map(idx => ({ ...value[idx], field: idx }));
};

const DataType = {
  TEXT: { id: "text", name: "Text" },
  NUMBER: { id: "number", name: "Number" },
  BOOLEAN: { id: "boolean", name: "Boolean" },
  SELECT: { id: "select", name: "Select" },
  DATE: { id: "date", name: "Date" },
  OBJECT: { id: "object", name: "Object" },
  COLLECTION: { id: "collection", name: "Collection" },
};

const choicesDataType = Object.keys(DataType).map(idx => DataType[idx]);

const getApplicableKeys = valueList => {
  let result = valueList.map(
    ({
      dataType,
      field,
      required,
      minLength,
      maxLength,
      minValue,
      maxValue,
      choices,
      multiple,
      regex,
      regexError,
      defaultValue,
    }) => {
      let additionalField = { dataType, field };
      switch (dataType) {
        case DataType.TEXT.id:
          return { ...additionalField, required, minLength, maxLength, regex, regexError, defaultValue };
        case DataType.NUMBER.id:
          return { ...additionalField, required, minValue, maxValue, regex, regexError, defaultValue };
        case DataType.BOOLEAN.id:
          return { ...additionalField, dataType, field, required, defaultValue };
        case DataType.SELECT.id:
          return { ...additionalField, required, choices, multiple, defaultValue };
        case DataType.DATE.id:
          return { ...additionalField, required, defaultValue };
        case DataType.COLLECTION.id:
          return { ...additionalField, required, defaultValue };
        case DataType.OBJECT.id:
          return { ...additionalField, required, defaultValue };
        default:
          return { ...additionalField, minLength, maxLength, regex, regexError, defaultValue };
      }
    }
  );
  return result;
};

const useStyles = makeStyles(_ => ({
  content: {
    margin: "1.5em",
  },
}));

const SettingView = props => {
  const { location = {} } = props;
  const settingResource = location.state?.settingResource || "";
  // state
  const [rows, setRows] = useState([{}]);
  const [version, setVersion] = useState(1);
  const [errors, setErrors] = useState([]);

  // api call
  const { data: setting = {} } = useQuery({
    url: `settings/${SettingKeys.ENTITY_FIELD_VALIDATION}`,
    params: { resource: settingResource },
    version,
  });

  const choicesSetting = JSON.parse(setting.choices || "[]");
  const valueSetting = JSON.parse(setting.value || "[]");

  useEffect(() => {
    let rows = [];
    if (!isEmpty(valueSetting)) {
      rows = getValueList(valueSetting);
    } else {
      rows = [];
    }
    setRows(rows);
  }, [JSON.stringify(valueSetting)]);

  // hooks
  const [mutate] = useMutation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const validate = (newValues = [], cb) => {
    let errors = [];
    // Check if any field is duplicate
    errors = findDuplicates(newValues.map(e => e.field.trim()));

    if (!isEmpty(errors)) {
      dispatch(showNotification("Duplicate fields: [ " + errors + " ]", { type: "danger" }));
      cb && cb();
      return false;
    }
    // Validate dataType against choices dataType
    newValues.forEach(formValue => {
      const { field, dataType } = formValue;
      const choice = choicesSetting.find(c => c.id === field);

      if (!isEmpty(choice)) {
        if (choice.dataType !== dataType) {
          errors.push({ field, dataType: choice.dataType, providedDataType: dataType });
        }
      }
    });
    if (!isEmpty(errors)) {
      setErrors(errors);
      cb && cb();
      return false;
    }
    return true;
  };

  const onSubmit = (values = [], form, onSubmitComplete) => {
    let noErrors = validate(values, onSubmitComplete);
    if (noErrors) {
      // Get applicable key-value pairs based on dataType
      values = getApplicableKeys(values);
      // Filter out empty rows, then remove keys whose value is empty
      let settingValues = values
        .filter(e => !isEmpty(e))
        .reduce((acc, { field, ...e }) => {
          let restValues = Object.keys(e)
            .filter(key => !isEmpty(e[key]))
            .reduce((acc, key) => ({ ...acc, [key]: e[key] }), {});
          return {
            ...acc,
            [field]: restValues, // restValues = {}, e.g. restValues = {dataType: "text", minLength: 1 ...}
          };
        }, {});
      const options = {
        url: `settings/${setting.key}`,
        method: "put",
        data: { ...setting, value: JSON.stringify(settingValues) },
      };
      mutate(options, {
        onSuccess: () => {
          setVersion(version => version + 1);
          onSubmitComplete();
          dispatch(showNotification("Setting updated successfully"));
        },
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        dataKey: "field",
        title: "Field",
        inputType: "select-input",
        choices: choicesSetting,
        render: value => choicesSetting.find(e => e.id === value)?.name,
        validate: value => !value && "Required",
      },
      {
        dataKey: "dataType",
        title: "Data Type",
        inputType: "select-input",
        choices: choicesDataType,
        render: value => choicesDataType.find(e => e.id === value)?.name,
        validate: value => !value && "Required",
      },
      { dataKey: "required", title: "Required", inputType: "text-input", render: value => renderBooleanValue(value) },
      {
        dataKey: "minValue",
        title: "Min Value",
        inputType: "text-input",
        disabled: row => row.dataType !== "number",
      },
      { dataKey: "maxValue", title: "Max Value", inputType: "text-input", disabled: row => row.dataType !== "number" },
      {
        dataKey: "minLength",
        title: "Min Length",
        inputType: "text-input",
        options: { type: "number" },
        disabled: row => row.dataType !== "text",
      },
      {
        dataKey: "maxLength",
        title: "Max Length",
        inputType: "text-input",
        options: { type: "number" },
        disabled: row => row.dataType !== "text",
      },
      { dataKey: "choices", title: "Choices", inputType: "text-input", disabled: row => row.dataType !== "select" },
      {
        dataKey: "multiple",
        title: "Multiple",
        inputType: "boolean-input",
        disabled: row => row.dataType !== "select",
        render: value => renderBooleanValue(value),
      },
      {
        dataKey: "regex",
        title: "Regex",
        inputType: "text-input",
        disabled: row => row.dataType !== "text",
      },
      {
        dataKey: "regexError",
        title: "Regex Error",
        inputType: "text-input",
        disabled: row => row.dataType !== "text",
      },
      { dataKey: "defaultValue", title: "Default Value", inputType: "text-input" },
    ],
    [JSON.stringify(choicesSetting)]
  );

  return (
    <div>
      <PageHeader title={setting.name || ""} />

      {!isEmpty(errors) && (
        <SweetAlert warning title="DataType mismatch" onConfirm={() => setErrors([])}>
          <div style={{ textAlign: "left" }}>
            {errors.map((error, idx) => (
              <Typography
                key={
                  idx
                }>{`Field: ${error.field}, Required DataType: ${error.dataType}, Provided DataType: ${error.providedDataType}`}</Typography>
            ))}
          </div>
        </SweetAlert>
      )}

      <div className={classes.content}>
        <MuiTable
          key={JSON.stringify(rows)}
          columns={columns}
          rows={rows}
          editable
          enableRowAddition
          showEditableActions
          rowAddCount={1}
          onSubmit={onSubmit}
          fontSize={16}
        />
      </div>

      <PageFooter style={{ marginLeft: "1.5em" }}>
        <BackButton variant="contained" />
      </PageFooter>
    </div>
  );
};

export default SettingView;
